import { Alert, Anchor, rem, Text } from '@mantine/core'
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

/**
 * Shown above a signup form when the visitor already has a session.
 *
 * Deliberately a notice rather than a redirect: someone here may genuinely want
 * a second account, and bouncing them to the dashboard — which is what the apps
 * used to do — gives them no way to tell that is what happened.
 *
 * Takes InviteBanner's shape — a filled Alert with a hairline at `radius="md"`,
 * about a line tall — but in tan rather than its blue. The auth pages sit on
 * `tan.0`, so a warm fill reads as part of the page instead of pasted onto it,
 * and this is information rather than an error. `tan.1` is only a few steps off
 * the page colour, so the `tan.4` edge is what separates the band; go to `tan.2`
 * if it needs more presence.
 *
 * Logging out is the only action offered — anyone who fills in the form without
 * it ends up back in the account they are already in.
 */
export default function SignedInNotice({
  email,
  logoutHref,
}: {
  email?: string
  /** Should return to the signup page, so logging out lands where they started. */
  logoutHref: string
}) {
  return (
    <Alert
      w="100%"
      mb="lg"
      py="sm"
      px="md"
      radius="md"
      variant="filled"
      color="tan.1"
      icon={<FontAwesomeIcon icon={faCircleInfo} color="black" />}
      styles={{
        root: { border: '1px solid var(--mantine-color-tan-4)' },
        // Alert's default icon gap is sized for a title-plus-body alert; this one
        // is a single sentence, so it only needs the text set off from the mark.
        icon: { marginInlineEnd: 'var(--mantine-spacing-sm)', marginTop: rem(1) },
        body: { gap: 0 },
      }}
    >
      <Text c="black" fz="sm" ta="left">
        You&rsquo;re already signed in{email ? ' as ' : ''}
        {email ? <b>{email}</b> : null}.{' '}
        <Anchor href={logoutHref} inherit fw={700} className="simple-link-blue">
          Log out first
        </Anchor>{' '}
        to create a different account.
      </Text>
    </Alert>
  )
}
